import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';

type Variant = 'primary' | 'outline' | 'ghost' | 'danger' | 'subtle';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: ReactNode;
  iconRight?: ReactNode;
}

/**
 * One primary action per view (§10.4/6): `primary` is the filled button and
 * should appear once per screen. Everything else is outline or ghost.
 *
 * `danger` is one of only two things allowed to use accent red — the other is
 * the "live on the site" dot. A palette where red appears six times teaches
 * people to ignore red.
 */
const VARIANTS: Record<Variant, string> = {
  // The filled buttons carry a hairline inner highlight so they read as a lit
  // surface rather than a flat block of colour. It is the one place this
  // interface acknowledges a light direction, and it is what separates a
  // button that looks drawn from one that looks pressed out of the page.
  primary:
    'bg-brand-500 text-white shadow-[inset_0_1px_0_rgb(255_255_255_/_0.18)] hover:bg-brand-600 active:bg-brand-700 disabled:bg-brand-500/40 disabled:shadow-none dark:disabled:bg-brand-700/40',
  outline:
    'border border-line bg-surface text-strong hover:border-line-strong hover:bg-raised active:bg-line/40',
  ghost: 'text-muted hover:bg-raised hover:text-strong active:bg-line/50',
  danger:
    'bg-accent-500 text-white shadow-[inset_0_1px_0_rgb(255_255_255_/_0.18)] hover:bg-accent-600 active:bg-accent-700',
  subtle:
    'bg-brand-500/10 text-brand-700 hover:bg-brand-500/16 active:bg-brand-500/24 dark:text-brand-200',
};

const SIZES: Record<Size, string> = {
  // 32px tall is dense-but-clickable for a desktop tool; the icon-only variant
  // below pads out to a comfortable square rather than shrinking further.
  sm: 'h-8 px-3 text-ui gap-1.5',
  md: 'h-10 px-4 text-ui gap-2',
  lg: 'h-12 px-6 text-body gap-2',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'outline', size = 'md', loading, icon, iconRight, className, children, disabled, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      // Disable during async work so a double-click cannot fire twice.
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex cursor-pointer select-none items-center justify-center rounded-control font-medium',
        'transition-ui duration-instant ease-out',
        // A press you can feel. 1.5% is below the threshold where it reads as
        // an animation and above the one where it reads as nothing.
        'active:scale-[0.985] disabled:active:scale-100',
        'disabled:cursor-not-allowed disabled:opacity-60',
        VARIANTS[variant],
        SIZES[size],
        !children && (size === 'sm' ? 'w-8 px-0' : size === 'lg' ? 'w-12 px-0' : 'w-10 px-0'),
        className,
      )}
      {...props}
    >
      {loading ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" /> : icon}
      {children}
      {!loading && iconRight}
    </button>
  );
});
